"use client";

import React, { useEffect, useState } from "react";
import type { GuestActivityBookingDTO, GuestActivityDTO, PublicActivityDTO } from "@/lib/types";
import { formatMoney, fromMinorUnits } from "@/lib/money";
import { useTracking } from "@/components/TrackingProvider";
import { useI18n } from "@/i18n/I18nProvider";
import { localizeError } from "@/i18n/errors";

/** A visitor books seats on an activity without staying (no stay, pay at the venue). */
export function PublicActivityBookingModal({
  hostname,
  activity,
  onClose,
}: {
  hostname: string;
  activity: PublicActivityDTO;
  onClose: () => void;
}) {
  const { t, locale, formatDate } = useI18n();
  const { track } = useTracking();
  const [detail, setDetail] = useState<GuestActivityDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionRef, setSessionRef] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<GuestActivityBookingDTO | null>(null);

  useEffect(() => {
    let live = true;
    fetch(`/api/public/sites/activities?hostname=${encodeURIComponent(hostname)}`)
      .then((r) => r.json())
      .then((json) => {
        if (!live) return;
        const found = (json.data as GuestActivityDTO[] | undefined)?.find((a) => a.catalog_item_id === activity.id) ?? null;
        setDetail(found);
        setQuantity(found?.min_pax || 1);
      })
      .catch(() => live && setDetail(null))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [hostname, activity.id]);

  const minQty = detail?.min_pax || 1;
  const session = detail?.sessions.find((s) => s.session_reference === sessionRef) ?? null;
  const maxQty = session ? Math.max(minQty, Math.min(detail?.max_pax ?? Infinity, session.available_quantity)) : minQty;
  const bookable = (s: GuestActivityDTO["sessions"][number]) => s.available_quantity >= minQty;

  const day = (iso: string) => {
    const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
    return formatDate(new Date(y, m - 1, d), { weekday: "short", day: "numeric", month: "short" });
  };
  const time = (a: string, b: string) => `${a.slice(11, 16)} – ${b.slice(11, 16)}`;

  const pickSession = (ref: string) => {
    setSessionRef(ref);
    setQuantity(minQty);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/public/sites/activity-bookings?hostname=${encodeURIComponent(hostname)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_reference: session.session_reference,
          quantity,
          guest_name: name,
          guest_phone: phone,
          guest_email: email || undefined,
          guest_note: note || undefined,
        }),
      });
      const json = await res.json();
      if (json.success) {
        const booked = json.data as GuestActivityBookingDTO;
        setDone(booked);
        // Pay at the venue: a lead for the owner, not a purchase.
        track("generate_lead", {
          lead_type: "activity_booking",
          value: fromMinorUnits(booked.total, booked.currency),
          currency: booked.currency,
          items: [{ item_id: activity.id, item_name: booked.activity_name, quantity: booked.quantity, price: fromMinorUnits(booked.unit_price, booked.currency) }],
        });
      }
      else setError(res.status === 429 ? t("publicsite.activities.errors.tooMany") : localizeError(locale, json.error));
    } catch {
      setError(t("publicsite.activities.errors.network"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ps-modal-back" onClick={onClose}>
      <div className="ps-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ps-modal-head">
          <div>
            <h3>{t("publicsite.dayTrip.title", { name: activity.name })}</h3>
            <p className="ps-muted">{t("publicsite.dayTrip.subtitle")}</p>
          </div>
          <button type="button" className="ps-x" onClick={onClose} aria-label={t("publicsite.booking.success.close")}>{"✕"}</button>
        </div>

        {done ? (
          <div className="ps-success">
            <div style={{ fontSize: 32 }}>{"🎉"}</div>
            <h4>{t("publicsite.activities.modal.confirmed")}</h4>
            <p>{t("publicsite.dayTrip.thanks", { name })}</p>
            <div className="ps-box" style={{ textAlign: "left" }}>
              <div className="line"><span className="ps-muted">{t("publicsite.activities.modal.reference")}</span><strong>{done.booking_reference}</strong></div>
              <div className="line"><span className="ps-muted">{t("publicsite.activities.modal.dateTime")}</span><span>{day(done.session_starts_at)} · {time(done.session_starts_at, done.session_ends_at)}</span></div>
              <div className="line"><span className="ps-muted">{t("publicsite.activities.modal.quantity")}</span><span>{t("publicsite.activities.seats", { count: done.quantity })}</span></div>
              <div className="line total"><span>{t("publicsite.activities.modal.total")}</span><strong>{formatMoney(done.total, done.currency)}</strong></div>
            </div>
            <p className="ps-muted">{t("publicsite.dayTrip.payAtVenue")}</p>
            <div className="ps-actions"><button type="button" className="ps-btn ps-btn-primary" onClick={onClose}>{t("publicsite.activities.modal.done")}</button></div>
          </div>
        ) : loading ? (
          <p className="ps-muted">{t("publicsite.dayTrip.loading")}</p>
        ) : !detail || detail.sessions.length === 0 ? (
          <p className="ps-muted">{t("publicsite.dayTrip.noSessions")}</p>
        ) : (
          <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
            <div className="ps-field">
              <label>{t("publicsite.dayTrip.pickSession")}</label>
              <div style={{ display: "grid", gap: 6 }}>
                {detail.sessions.map((s) => {
                  const ok = bookable(s);
                  return (
                    <label key={s.session_reference} className="ps-box" style={{ cursor: ok ? "pointer" : "not-allowed", opacity: ok ? 1 : 0.55, flexDirection: "row", alignItems: "center", gap: 10 }}>
                      <input type="radio" name="session" disabled={!ok} checked={sessionRef === s.session_reference} onChange={() => pickSession(s.session_reference)} />
                      <span style={{ flex: 1 }}>
                        <strong>{day(s.starts_at)} · {time(s.starts_at, s.ends_at)}</strong>
                        <span className="ps-muted" style={{ display: "block" }}>
                          {ok ? t("publicsite.activities.seatsAvailable", { count: s.available_quantity }) : t("publicsite.activities.soldOut")} · {t("publicsite.activities.perSeat", { amount: formatMoney(s.effective_price, s.currency) })}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {session && (
              <div className="ps-field">
                <label>{t("publicsite.activities.modal.numberOfSeats")}</label>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <button type="button" className="ps-btn ps-btn-ghost ps-btn-sm" disabled={quantity <= minQty} onClick={() => setQuantity((q) => Math.max(minQty, q - 1))}>{"−"}</button>
                  <strong style={{ minWidth: 28, textAlign: "center" }}>{quantity}</strong>
                  <button type="button" className="ps-btn ps-btn-ghost ps-btn-sm" disabled={quantity >= maxQty} onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}>{"+"}</button>
                  <span className="ps-muted">{t("publicsite.dayTrip.range", { min: minQty, max: maxQty })}</span>
                </div>
              </div>
            )}

            <div className="ps-field">
              <label>{t("publicsite.dayTrip.name")}</label>
              <input className="ps-input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
            </div>
            <div className="ps-field">
              <label>{t("publicsite.dayTrip.phone")}</label>
              <input className="ps-input" type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required maxLength={30} />
            </div>
            <div className="ps-field">
              <label>{t("publicsite.dayTrip.email")}</label>
              <input className="ps-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={200} />
            </div>
            <div className="ps-field">
              <label>{t("publicsite.activities.modal.notes")}</label>
              <textarea className="ps-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} />
            </div>

            {session && (
              <div className="ps-box">
                <div className="line total"><span>{t("publicsite.activities.modal.totalFor", { seats: t("publicsite.activities.seats", { count: quantity }) })}</span><strong>{formatMoney(session.effective_price * quantity, session.currency)}</strong></div>
                <span className="ps-muted">{t("publicsite.dayTrip.payAtVenue")}</span>
              </div>
            )}
            {error && <p role="alert" style={{ color: "#b42318", fontSize: 13 }}>{error}</p>}
            <div className="ps-actions">
              <button type="button" className="ps-btn ps-btn-ghost" onClick={onClose}>{t("publicsite.activities.cancel") }</button>
              <button type="submit" className="ps-btn ps-btn-primary" disabled={!session || submitting}>
                {submitting ? t("publicsite.activities.modal.reserving") : t("publicsite.activities.modal.confirm")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
